<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Timesheets</h5>
        <p class="page-sub">Submit and manage weekly project time entries</p>
      </div>
      <div class="header-actions">
        <button class="btn-outline" @click="exportCsv" :disabled="!timesheets.length">
          <font-awesome-icon icon="file-csv" class="me-1" /> Export CSV
        </button>
        <button class="btn-outline" @click="exportPdf" :disabled="!timesheets.length">
          <font-awesome-icon icon="file-pdf" class="me-1" /> Export PDF
        </button>
        <button class="btn-primary" @click="openWeekPicker">
          <font-awesome-icon icon="plus" class="me-1" /> New Timesheet
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="filter-bar">
      <input
        type="text"
        v-model="filterSearch"
        class="filter-input filter-input--search"
        placeholder="Search employee name…"
        @keyup.enter="fetchTimesheets"
      />
      <div class="filter-ms-wrap">
        <Multiselect
          v-model="filterEmployee"
          :options="employeeOptions"
          :searchable="true"
          :clearOnSelect="false"
          :closeOnSelect="true"
          placeholder="All Employees"
          valueProp="value"
          label="label"
          trackBy="label"
          @select="fetchTimesheets"
          @clear="fetchTimesheets"
          class="filter-ms"
        />
      </div>
      <div class="filter-ms-wrap">
        <Multiselect
          v-model="filterProject"
          :options="projectOptions"
          :searchable="true"
          :clearOnSelect="false"
          :closeOnSelect="true"
          placeholder="All Projects"
          valueProp="value"
          label="label"
          trackBy="label"
          @select="fetchTimesheets"
          @clear="fetchTimesheets"
          class="filter-ms"
        />
      </div>
      <select v-model="filterStatus" @change="fetchTimesheets" class="filter-select">
        <option value="">All Statuses</option>
        <option value="draft">Draft</option>
        <option value="submitted">Submitted</option>
        <option value="approved">Approved</option>
        <option value="rejected">Rejected</option>
      </select>
      <input type="date" v-model="filterFrom" class="filter-input" style="max-width:160px" />
      <input type="date" v-model="filterTo" class="filter-input" style="max-width:160px" />
      <button class="btn-apply" @click="fetchTimesheets">Filter</button>
    </div>

    <!-- Bulk action bar -->
    <div v-if="selectedIds.length" class="bulk-bar">
      <span class="bulk-count">{{ selectedIds.length }} selected</span>
      <button
        v-if="bulkSubmitIds.length"
        class="btn-bulk btn-bulk--primary"
        @click="bulkSubmit"
        :disabled="saving"
      >Submit {{ bulkSubmitIds.length }} for Approval</button>
      <button
        v-if="bulkApproveIds.length"
        class="btn-bulk btn-bulk--success"
        @click="bulkApprove"
        :disabled="saving"
      >Approve {{ bulkApproveIds.length }}</button>
      <button class="btn-bulk btn-bulk--ghost" @click="selectedIds = []">Clear</button>
    </div>

    <div class="card">
      <div v-if="loading" class="loading-state"><div class="spinner"></div><p>Loading...</p></div>
      <div v-else-if="!timesheets.length" class="empty-state">
        <font-awesome-icon icon="clock" class="empty-icon" />
        <p>No timesheets found</p>
      </div>
      <table v-else class="data-table">
        <thead>
          <tr>
            <th class="cb-col">
              <input type="checkbox" :checked="allSelected" :indeterminate.prop="someSelected" @change="toggleAll" />
            </th>
            <th>Employee</th>
            <th>Project</th>
            <th>Week Starting</th>
            <th class="amount-col">Total Hours</th>
            <th>Status</th>
            <th>Submitted</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="t in timesheets" :key="t.id" :class="{ 'row--selected': selectedIds.includes(t.id) }">
            <td class="cb-col">
              <input type="checkbox" :value="t.id" v-model="selectedIds" />
            </td>
            <td>{{ t.employee ? t.employee.first_name + ' ' + t.employee.last_name : '—' }}</td>
            <td>
              <span v-if="t.project" class="project-tag">
                <span class="project-code">{{ t.project.project_code }}</span>
                {{ t.project.project_name }}
              </span>
              <span v-else class="text-dim">—</span>
            </td>
            <td>{{ formatDate(t.week_start) }}</td>
            <td class="amount-col">{{ t.total_hours }}h</td>
            <td><span :class="['status-badge', `status-badge--${t.status}`]">{{ t.status }}</span></td>
            <td>{{ t.submitted_at ? formatDate(t.submitted_at) : '—' }}</td>
            <td>
              <div class="row-actions">
                <button class="btn-link" @click="openTimesheet(t.id)">{{ t.status === 'draft' ? 'Edit' : 'View' }}</button>
                <button v-if="t.status === 'submitted'" class="btn-link btn-link--success" @click="approveTimesheet(t.id)">Approve</button>
                <button v-if="t.status === 'submitted'" class="btn-link btn-link--danger" @click="rejectTimesheet(t.id)">Reject</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Timesheet detail/edit modal -->
    <div class="modal-overlay" :class="{ open: detailOpen }" @click.self="detailOpen = false">
      <div class="modal-box modal-box--xl">
        <div class="modal-header">
          <span class="modal-title">Timesheet — Week of {{ selectedSheet?.week_start ? formatDate(selectedSheet.week_start) : '' }}</span>
          <button class="modal-close" @click="detailOpen = false">&times;</button>
        </div>
        <div class="modal-body" v-if="selectedSheet">
          <div class="ts-meta-row">
            <div class="ts-meta-item">
              <span class="ts-meta-label">Employee</span>
              <span class="ts-meta-value">{{ selectedSheet.employee ? selectedSheet.employee.first_name + ' ' + selectedSheet.employee.last_name : '—' }}</span>
            </div>
            <div class="ts-meta-item" v-if="selectedSheet.project">
              <span class="ts-meta-label">Project</span>
              <span class="ts-meta-value">
                <span class="project-code">{{ selectedSheet.project.project_code }}</span>
                {{ selectedSheet.project.project_name }}
              </span>
            </div>
            <div class="ts-meta-item">
              <span class="ts-meta-label">Week</span>
              <span class="ts-meta-value">{{ selectedSheet.week_start ? formatDate(selectedSheet.week_start) : '—' }}</span>
            </div>
          </div>
          <div class="ts-status-row">
            <span :class="['status-badge', `status-badge--${selectedSheet.status}`]">{{ selectedSheet.status }}</span>
            <span class="text-muted text-sm">Total: {{ totalEntryHours }}h</span>
          </div>

          <div v-if="selectedSheet.status === 'rejected' && selectedSheet.rejection_reason" class="rejection-banner">
            <font-awesome-icon icon="exclamation-circle" class="rejection-icon" />
            <div>
              <span class="rejection-label">Rejection Reason</span>
              <p class="rejection-text">{{ selectedSheet.rejection_reason }}</p>
            </div>
          </div>

          <!-- Entry rows -->
          <table class="data-table mt-12">
            <thead>
              <tr><th>WBS Task</th><th>Date</th><th>Hours</th><th>Billable</th><th>Description</th><th v-if="canEdit"></th></tr>
            </thead>
            <tbody>
              <tr v-for="(entry, idx) in editEntries" :key="idx">
                <td>
                  <select v-if="canEdit" v-model="entry.wbs_id" class="form-control form-control--sm">
                    <option value="">Select task</option>
                    <option v-for="w in wbsOptions" :key="w.id" :value="w.id">{{ w.code }} — {{ w.name }}</option>
                  </select>
                  <span v-else>{{ entry.wbs_element?.name || entry.wbs_id }}</span>
                </td>
                <td><input v-if="canEdit" type="date" v-model="entry.work_date" class="form-control form-control--sm" /><span v-else>{{ formatDate(entry.work_date) }}</span></td>
                <td><input v-if="canEdit" type="number" v-model="entry.hours" step="0.5" min="0.5" max="24" class="form-control form-control--sm" style="width:80px" /><span v-else>{{ entry.hours }}h</span></td>
                <td><input v-if="canEdit" type="checkbox" v-model="entry.billable" /><span v-else>{{ entry.billable ? 'Yes' : 'No' }}</span></td>
                <td><input v-if="canEdit" v-model="entry.description" class="form-control form-control--sm" placeholder="What did you work on?" /><span v-else>{{ entry.description || '—' }}</span></td>
                <td v-if="canEdit"><button class="btn-icon btn-icon--danger" @click="editEntries.splice(idx, 1)">✕</button></td>
              </tr>
            </tbody>
          </table>
          <button v-if="canEdit" class="btn-outline mt-8" @click="addEntryRow">+ Add Row</button>
        </div>
        <div class="modal-footer" v-if="selectedSheet">
          <button class="btn-outline" @click="detailOpen = false">Close</button>
          <button class="btn-outline" @click="printTimesheet" style="margin-right:auto">
            <font-awesome-icon icon="print" class="me-1" /> Print
          </button>
          <button v-if="canEdit" class="btn-outline" @click="saveEntries" :disabled="saving">Save Draft</button>
          <button v-if="canEdit" class="btn-primary" @click="submitTimesheet" :disabled="saving">Submit for Approval</button>
        </div>
      </div>
    </div>

    <!-- Reject modal -->
    <div class="modal-overlay" :class="{ open: rejectModal.open }" @click.self="rejectModal.open = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-header">
          <span class="modal-title">Reject Timesheet</span>
          <button class="modal-close" @click="rejectModal.open = false">&times;</button>
        </div>
        <div class="modal-body">
          <p class="reject-desc">Provide a reason so the employee knows what to fix.</p>
          <div class="form-group">
            <label>Rejection Reason <span class="text-optional">(optional)</span></label>
            <textarea v-model="rejectModal.notes" class="form-control" rows="3" placeholder="e.g. Missing descriptions on Tuesday entries…"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="rejectModal.open = false">Cancel</button>
          <button class="btn-danger" @click="doReject" :disabled="saving">Reject Timesheet</button>
        </div>
      </div>
    </div>

    <!-- Week picker modal -->
    <div class="modal-overlay" :class="{ open: weekPickerOpen }" @click.self="weekPickerOpen = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-header"><span class="modal-title">Select Week</span><button class="modal-close" @click="weekPickerOpen = false">&times;</button></div>
        <div class="modal-body">
          <div class="form-group">
            <label>Employee</label>
            <select v-model="newSheet.employee_id" class="form-control">
              <option value="">Select employee</option>
              <option v-for="e in employees" :key="e.id" :value="e.id">{{ e.first_name }} {{ e.last_name }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>Week Starting (Monday)</label>
            <input type="date" v-model="newSheet.week_start" class="form-control" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="weekPickerOpen = false">Cancel</button>
          <button class="btn-primary" @click="createOrOpenSheet" :disabled="saving">Open Timesheet</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import Multiselect from '@vueform/multiselect';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export default {
  name: 'TimesheetPage',
  components: { Multiselect },
  setup() { return { toast: useToast() }; },
  data() {
    return {
      timesheets: [], loading: false, saving: false,
      selectedIds: [],
      filterSearch: '', filterEmployee: null, filterProject: null,
      filterStatus: '', filterFrom: '', filterTo: '',
      detailOpen: false, weekPickerOpen: false,
      rejectModal: { open: false, id: null, notes: '' },
      selectedSheet: null, editEntries: [],
      employees: [], projects: [], wbsOptions: [],
      newSheet: { employee_id: '', week_start: '' },
    };
  },
  computed: {
    canEdit() { return this.selectedSheet && ['draft', 'rejected'].includes(this.selectedSheet.status); },
    totalEntryHours() { return this.editEntries.reduce((s, e) => s + parseFloat(e.hours || 0), 0).toFixed(1); },
    employeeOptions() {
      return this.employees.map(e => ({ value: e.id, label: `${e.first_name} ${e.last_name}` }));
    },
    projectOptions() {
      return this.projects.map(p => ({ value: p.id, label: `${p.project_code} — ${p.project_name}` }));
    },
    allSelected() {
      return this.timesheets.length > 0 && this.selectedIds.length === this.timesheets.length;
    },
    someSelected() {
      return this.selectedIds.length > 0 && this.selectedIds.length < this.timesheets.length;
    },
    bulkSubmitIds() {
      return this.selectedIds.filter(id => {
        const t = this.timesheets.find(x => x.id === id);
        return t && ['draft', 'rejected'].includes(t.status);
      });
    },
    bulkApproveIds() {
      return this.selectedIds.filter(id => {
        const t = this.timesheets.find(x => x.id === id);
        return t && t.status === 'submitted';
      });
    },
  },
  created() { this.fetchTimesheets(); this.loadEmployees(); this.loadProjects(); },
  methods: {
    async fetchTimesheets() {
      this.loading = true;
      try {
        const { data } = await axios.get('/timesheets', {
          params: {
            search:      this.filterSearch   || undefined,
            employee_id: this.filterEmployee || undefined,
            project_id:  this.filterProject  || undefined,
            status:      this.filterStatus   || undefined,
            from:        this.filterFrom     || undefined,
            to:          this.filterTo       || undefined,
          },
        });
        this.timesheets = data.data ?? [];
        this.selectedIds = [];
      } catch { this.toast.error('Failed to load timesheets.'); }
      finally { this.loading = false; }
    },
    async loadEmployees() {
      try { const { data } = await axios.get('/payroll/employees/active'); this.employees = data.data ?? []; }
      catch { /* silent */ }
    },
    async loadProjects() {
      try {
        const { data } = await axios.get('/projects');
        this.projects = data.data?.data ?? data.data ?? [];
      } catch { /* silent */ }
    },
    async openWeekPicker() { this.newSheet = { employee_id: '', week_start: '' }; this.weekPickerOpen = true; },
    async createOrOpenSheet() {
      if (!this.newSheet.employee_id || !this.newSheet.week_start) return this.toast.error('Select employee and week.');
      this.saving = true;
      try {
        const { data } = await axios.post('/timesheets/get-or-create', this.newSheet);
        this.weekPickerOpen = false;
        await this.openTimesheet(data.data.id);
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Error.'); }
      finally { this.saving = false; }
    },
    async openTimesheet(id) {
      const { data } = await axios.get(`/timesheets/${id}`);
      this.selectedSheet = data.data;
      this.editEntries   = (data.data.entries ?? []).map(e => ({ ...e, work_date: e.work_date ? e.work_date.slice(0, 10) : '' }));
      if (!this.wbsOptions.length) await this.loadWbsOptions();
      this.detailOpen    = true;
    },
    async loadWbsOptions() {
      try { const { data } = await axios.get('/projects'); const projects = data.data?.data ?? data.data ?? [];
        for (const p of projects.slice(0, 20)) {
          const w = await axios.get(`/projects/${p.id}/wbs`);
          this.wbsOptions.push(...(w.data.data ?? []));
        }
      } catch { /* silent */ }
    },
    addEntryRow() { this.editEntries.push({ wbs_id: '', work_date: '', hours: 8, billable: true, description: '' }); },
    async saveEntries() {
      this.saving = true;
      try {
        await axios.post(`/timesheets/${this.selectedSheet.id}/entries`, { entries: this.editEntries });
        this.toast.success('Entries saved.');
        const { data } = await axios.get(`/timesheets/${this.selectedSheet.id}`);
        this.selectedSheet = data.data;
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Error saving entries.'); }
      finally { this.saving = false; }
    },
    async submitTimesheet() {
      await this.saveEntries();
      this.saving = true;
      try {
        await axios.post(`/timesheets/${this.selectedSheet.id}/submit`);
        this.toast.success('Timesheet submitted for approval.');
        this.detailOpen = false;
        this.fetchTimesheets();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Error.'); }
      finally { this.saving = false; }
    },
    async approveTimesheet(id) {
      try { await axios.post(`/timesheets/${id}/approve`); this.toast.success('Approved.'); this.fetchTimesheets(); }
      catch (e) { this.toast.error(e.response?.data?.message ?? 'Error.'); }
    },
    rejectTimesheet(id) {
      this.rejectModal = { open: true, id, notes: '' };
    },
    async doReject() {
      this.saving = true;
      try {
        await axios.post(`/timesheets/${this.rejectModal.id}/reject`, { notes: this.rejectModal.notes });
        this.toast.success('Timesheet rejected.');
        this.rejectModal.open = false;
        this.fetchTimesheets();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Error.'); }
      finally { this.saving = false; }
    },
    toggleAll() {
      this.selectedIds = this.allSelected ? [] : this.timesheets.map(t => t.id);
    },
    async bulkSubmit() {
      if (!this.bulkSubmitIds.length) return;
      this.saving = true;
      try {
        const { data } = await axios.post('/timesheets/bulk-submit', { ids: this.bulkSubmitIds });
        this.toast.success(data.message ?? 'Timesheets submitted.');
        this.fetchTimesheets();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Bulk submit failed.'); }
      finally { this.saving = false; }
    },
    async bulkApprove() {
      if (!this.bulkApproveIds.length) return;
      this.saving = true;
      try {
        const { data } = await axios.post('/timesheets/bulk-approve', { ids: this.bulkApproveIds });
        this.toast.success(data.message ?? 'Timesheets approved.');
        this.fetchTimesheets();
      } catch (e) { this.toast.error(e.response?.data?.message ?? 'Bulk approve failed.'); }
      finally { this.saving = false; }
    },
    printTimesheet() {
      const s        = this.selectedSheet;
      const employee = s.employee ? `${s.employee.first_name} ${s.employee.last_name}` : '—';
      const project  = s.project  ? `${s.project.project_code} — ${s.project.project_name}` : '—';
      const week     = s.week_start ? this.formatDate(s.week_start) : '—';
      const status   = (s.status ?? '').charAt(0).toUpperCase() + (s.status ?? '').slice(1);

      const doc = new jsPDF();
      const brandBlue = [0, 74, 173];

      // Header bar
      doc.setFillColor(...brandBlue);
      doc.rect(0, 0, 210, 18, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('Timesheet', 14, 12);

      // Meta grid
      doc.setTextColor(120, 144, 176);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('EMPLOYEE',     14, 26);
      doc.text('PROJECT',      80, 26);
      doc.text('WEEK STARTING', 14, 38);
      doc.text('STATUS',       80, 38);

      doc.setTextColor(26, 43, 69);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(employee, 14, 32);
      doc.text(project,  80, 32);
      doc.text(week,     14, 44);
      doc.text(status,   80, 44);

      // Rejection reason (if applicable)
      let startY = 54;
      if (s.status === 'rejected' && s.rejection_reason) {
        doc.setFillColor(255, 245, 245);
        doc.setDrawColor(254, 202, 202);
        const lines = doc.splitTextToSize(`Rejection Reason: ${s.rejection_reason}`, 182);
        const boxH  = lines.length * 5 + 8;
        doc.roundedRect(14, startY, 182, boxH, 2, 2, 'FD');
        doc.setTextColor(220, 38, 38);
        doc.setFontSize(9);
        doc.text(lines, 18, startY + 6);
        startY += boxH + 6;
        doc.setDrawColor(200, 200, 200);
      }

      // Entries table
      autoTable(doc, {
        startY,
        head: [['WBS Task', 'Date', 'Hours', 'Billable', 'Description']],
        body: this.editEntries.map(e => [
          e.wbs_element?.name || e.wbs_element?.code || '—',
          e.work_date ? this.formatDate(e.work_date) : '—',
          `${e.hours}h`,
          e.billable ? 'Yes' : 'No',
          e.description || '—',
        ]),
        foot: [['', 'Total', `${this.totalEntryHours}h`, '', '']],
        headStyles:  { fillColor: brandBlue, fontSize: 8, fontStyle: 'bold' },
        footStyles:  { fillColor: [248, 250, 252], textColor: [26, 43, 69], fontStyle: 'bold', fontSize: 9 },
        bodyStyles:  { fontSize: 9 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: { 2: { halign: 'right' } },
        showFoot: 'lastPage',
      });

      const filename = `timesheet-${employee.replace(/\s+/g, '-').toLowerCase()}-${week.replace(/\s+/g, '-')}.pdf`;
      doc.save(filename);
    },
    exportCsv() {
      const headers = ['Employee', 'Project', 'Week Starting', 'Total Hours', 'Status', 'Submitted'];
      const rows = this.timesheets.map(t => [
        t.employee ? `${t.employee.first_name} ${t.employee.last_name}` : '',
        t.project  ? `${t.project.project_code} ${t.project.project_name}` : '',
        t.week_start ? this.formatDate(t.week_start) : '',
        t.total_hours ?? '',
        t.status ?? '',
        t.submitted_at ? this.formatDate(t.submitted_at) : '',
      ]);
      const csv = [headers, ...rows]
        .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
        .join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `timesheets-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    exportPdf() {
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text('Timesheets', 14, 16);
      doc.setFontSize(9);
      doc.setTextColor(120);
      doc.text(`Exported ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`, 14, 22);
      autoTable(doc, {
        startY: 27,
        head: [['Employee', 'Project', 'Week Starting', 'Hours', 'Status', 'Submitted']],
        body: this.timesheets.map(t => [
          t.employee ? `${t.employee.first_name} ${t.employee.last_name}` : '—',
          t.project  ? `${t.project.project_code} — ${t.project.project_name}` : '—',
          t.week_start ? this.formatDate(t.week_start) : '—',
          t.total_hours ? `${t.total_hours}h` : '—',
          t.status ?? '—',
          t.submitted_at ? this.formatDate(t.submitted_at) : '—',
        ]),
        headStyles: { fillColor: [0, 74, 173], fontSize: 9, fontStyle: 'bold' },
        bodyStyles: { fontSize: 9 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: { 3: { halign: 'right' } },
      });
      doc.save(`timesheets-${new Date().toISOString().slice(0, 10)}.pdf`);
    },
    formatDate(d) { return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; },
  },
};
</script>

<style src="@vueform/multiselect/themes/default.css"></style>

<style scoped>
/* ── Layout ─────────────────────────────────────────────────────────── */
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.header-actions { display:flex; align-items:center; gap:10px; }
.me-1 { margin-right:4px; }
.mt-8  { margin-top:8px; }
.mt-12 { margin-top:12px; }

/* ── Filter bar ──────────────────────────────────────────────────────── */
.filter-bar    { display:flex; gap:12px; margin-bottom:16px; flex-wrap:wrap; align-items:center; }
.filter-input  { flex:1; min-width:180px; padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; outline:none; }
.filter-input--search { min-width:200px; max-width:240px; }
.filter-input:focus { border-color:#004AAD; }
.filter-select { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; background:#fff; cursor:pointer; outline:none; }
.filter-ms-wrap { min-width:200px; max-width:240px; flex:1; }

/* Multiselect overrides */
.filter-ms {
  --ms-border-color: #dde5f0;
  --ms-border-width: 1.5px;
  --ms-radius: 8px;
  --ms-font-size: 13px;
  --ms-py: 7px;
  --ms-px: 12px;
  --ms-ring-width: 0px;
  --ms-option-font-size: 13px;
  --ms-option-bg-selected: #eff6ff;
  --ms-option-color-selected: #004AAD;
  --ms-option-bg-pointed: #f4f7fb;
  --ms-tag-bg: #eff6ff;
  --ms-tag-color: #004AAD;
  --ms-dropdown-border-color: #dde5f0;
  --ms-dropdown-radius: 8px;
  --ms-placeholder-color: #7a90b0;
}
.filter-ms.multiselect.is-active {
  --ms-border-color: #004AAD;
}

/* ── Bulk action bar ────────────────────────────────────────────────── */
.bulk-bar { display:flex; align-items:center; gap:10px; padding:10px 16px; background:#eff6ff; border:1.5px solid #bfdbfe; border-radius:10px; margin-bottom:12px; flex-wrap:wrap; }
.bulk-count { font-size:13px; font-weight:600; color:#1d4ed8; margin-right:4px; }
.btn-bulk { font-size:12.5px; font-weight:600; padding:7px 16px; border-radius:7px; border:none; cursor:pointer; }
.btn-bulk--primary { background:#004AAD; color:#fff; }
.btn-bulk--primary:hover:not(:disabled) { background:#003b8e; }
.btn-bulk--success { background:#15803d; color:#fff; }
.btn-bulk--success:hover:not(:disabled) { background:#166534; }
.btn-bulk--ghost { background:transparent; color:#64748b; border:1.5px solid #dde5f0; background:#fff; }
.btn-bulk--ghost:hover { background:#f4f7fb; }
.btn-bulk:disabled { opacity:.55; cursor:not-allowed; }

/* ── Card / table ───────────────────────────────────────────────────── */
.card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.loading-state,.empty-state { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; color:#7a90b0; gap:10px; }
.empty-icon { font-size:40px; color:#dde5f0; }
.empty-state p { font-size:15px; font-weight:600; color:#1a2b45; margin:0; }
.data-table { width:100%; border-collapse:collapse; }
.data-table th { padding:11px 16px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; text-align:left; white-space:nowrap; }
.data-table td { padding:13px 16px; font-size:13px; color:#1a2b45; border-bottom:1px solid #f0f4f8; vertical-align:middle; }
.cb-col { width:40px; padding-left:16px; padding-right:4px; }
.row--selected td { background:#eff6ff; }
.data-table input[type="checkbox"] { width:16px; height:16px; cursor:pointer; accent-color:#004AAD; }
.data-table.mt-12 { margin-top:12px; }
.data-table th.amount-col { text-align:right; }
.amount-col { text-align:right; font-variant-numeric:tabular-nums; }
.project-tag { display:inline-flex; align-items:center; gap:6px; font-size:13px; color:#1a2b45; }
.project-code { font-family:monospace; font-size:11.5px; font-weight:700; color:#004AAD; background:#eff6ff; padding:2px 7px; border-radius:5px; }
.text-dim { color:#7a90b0; font-size:13px; }

/* ── Status badge ───────────────────────────────────────────────────── */
.status-badge { display:inline-block; font-size:11.5px; font-weight:600; border-radius:20px; padding:3px 10px; white-space:nowrap; }
.status-badge--draft     { background:#f1f5f9; color:#64748b; }
.status-badge--submitted { background:#eff6ff; color:#1d4ed8; }
.status-badge--approved  { background:#dcfce7; color:#15803d; }
.status-badge--rejected  { background:#fee2e2; color:#dc2626; }

/* ── Buttons ────────────────────────────────────────────────────────── */
.row-actions { display:flex; gap:6px; }
.btn-link { background:none; border:none; font-size:12.5px; font-weight:600; color:#004AAD; cursor:pointer; padding:4px 6px; border-radius:5px; }
.btn-link:hover { background:#eff6ff; }
.btn-link--danger { color:#dc2626; }
.btn-link--danger:hover { background:#fee2e2; }
.btn-link--success { color:#15803d; }
.btn-link--success:hover { background:#dcfce7; }
.btn-icon { width:30px; height:30px; border-radius:7px; border:1px solid #e4eaf2; background:#fff; color:#1a2b45; display:inline-flex; align-items:center; justify-content:center; font-size:13px; cursor:pointer; }
.btn-icon:hover { background:#f4f7fb; }
.btn-icon--danger:hover { background:#fee2e2; border-color:#fecaca; color:#dc2626; }
.btn-primary { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary:hover:not(:disabled) { background:#003b8e; }
.btn-primary:disabled { opacity:.55; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; }
.btn-outline:hover { background:#f4f7fb; }
.btn-outline.mt-8 { margin-top:8px; }
.btn-apply { background:#004AAD; color:#fff; border:none; padding:9px 14px; border-radius:8px; font-size:13px; font-weight:600; cursor:pointer; }
.btn-apply:hover { background:#003b8e; }

/* ── Modal ──────────────────────────────────────────────────────────── */
.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:none; align-items:center; justify-content:center; padding:20px; }
.modal-overlay.open { display:flex; }
.modal-box { background:#fff; border-radius:14px; width:100%; max-width:520px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--sm { max-width:420px; }
.modal-box--xl { max-width:900px; }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 24px; border-bottom:1px solid #e4eaf2; position:sticky; top:0; background:#fff; z-index:1; }
.modal-title  { font-size:16px; font-weight:700; color:#1a2b45; }
.modal-close  { background:transparent; border:none; font-size:22px; color:#7a90b0; cursor:pointer; line-height:1; }
.modal-body   { padding:20px 24px; }
.modal-footer { display:flex; justify-content:flex-end; gap:10px; padding:16px 24px; border-top:1px solid #e4eaf2; position:sticky; bottom:0; background:#fff; }
.form-group   { margin-bottom:14px; }
.form-group label { display:block; font-size:12.5px; font-weight:600; color:#4a5568; margin-bottom:5px; }
.form-control { width:100%; padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; }
.form-control:focus { border-color:#004AAD; }
.form-control--sm { padding:6px 10px; font-size:12.5px; }

/* ── Timesheet modal meta ───────────────────────────────────────────── */
.ts-meta-row { display:flex; flex-wrap:wrap; gap:24px; padding:14px 0 16px; border-bottom:1px solid #f0f4f8; margin-bottom:14px; }
.ts-meta-item { display:flex; flex-direction:column; gap:3px; }
.ts-meta-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
.ts-meta-value { font-size:13px; font-weight:600; color:#1a2b45; display:flex; align-items:center; gap:6px; }
.ts-status-row { display:flex; align-items:center; gap:12px; margin-bottom:4px; }

/* ── Rejection banner (inside modal) ───────────────────────────────── */
.rejection-banner { display:flex; align-items:flex-start; gap:10px; background:#fff5f5; border:1.5px solid #fecaca; border-radius:8px; padding:12px 14px; margin:12px 0; }
.rejection-icon { color:#dc2626; font-size:16px; margin-top:2px; flex-shrink:0; }
.rejection-label { display:block; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#dc2626; margin-bottom:3px; }
.rejection-text { font-size:13px; color:#7f1d1d; margin:0; }

/* ── Reject modal ───────────────────────────────────────────────────── */
.reject-desc { font-size:13px; color:#7a90b0; margin:0 0 14px; }
.text-optional { font-size:11.5px; font-weight:400; color:#7a90b0; }
.btn-danger { display:inline-flex; align-items:center; background:#dc2626; color:#fff; border:none; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; cursor:pointer; }
.btn-danger:hover:not(:disabled) { background:#b91c1c; }
.btn-danger:disabled { opacity:.55; cursor:not-allowed; }

/* ── Spinner ────────────────────────────────────────────────────────── */
.spinner { width:32px; height:32px; border:3px solid #e4eaf2; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform:rotate(360deg); } }
</style>
